import React, { useEffect, useReducer } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { getEmailCustomization, putEmailCustomization } from '../../../api';
import { useConfig } from '../../../contexts/config';
import { useCurrentProject } from '../../../contexts/settings';
import { useProductAgreement } from '../../../hooks';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { VerticalHorizontalCenterBlock } from '../../../styled';
import { AgreementNotification, CodeEditor, FormFooter, HeaderTitle, Preloader } from '../../ui';
import SettingsBar from '../HTMLEditorFrame';
import { emailPreviewReducer, initialState, selectCurrentEmail } from './reducer';
import { CodeEditorFrame, CustomizationFrameWrapper, StyledIframe } from './styles';
import { ActionType, EmailTypes } from './types';
import { MAP_EMAILS_TO_FIELDS } from './utils';

const EmailPreviewFrame = ({ history }: RouteComponentProps): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const { merchantId } = useConfig();
  const { currentProject } = useCurrentProject();
  const { fetchingAgreement, isAgreementSigned } = useProductAgreement(merchantId);
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const [state, dispatch] = useReducer(emailPreviewReducer, initialState);

  useEffect(() => {
    if (currentProject) {
      getEmailCustomization(currentProject.id).then((data) => {
        // ToDo: remove after deploy https://xsolla.atlassian.net/browse/LOGIN-4001
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { passwordless_auth_email, ...clearData } = data;
        dispatch({ type: ActionType.getEmailsSuccess, payload: clearData });
      });
    }
  }, [currentProject]);

  const backToNavigation = () =>
    currentProject && history.push({ pathname: navigationPath(currentProject.id) });

  const switchPreview = () => dispatch({ type: ActionType.changePreview });

  const disabledTemplates = [EmailTypes.blockedAccount, EmailTypes.custom];

  const EmailTypeOptions = Object.keys(EmailTypes)
    .filter((key) => !disabledTemplates.includes(key as EmailTypes))
    .map((key) => ({
      children: intl.messages[`email-customization.settings.bar.options.${key}`],
      onClick: () => dispatch({ type: ActionType.setEmailType, payload: key })
    }));

  const onCodeChange = (value: string) => {
    dispatch({
      type: ActionType.updateEmails,
      payload: { [MAP_EMAILS_TO_FIELDS[state.emailType]]: value }
    });
  };

  const SubmitHandle = () => {
    if (currentProject && state.emailCustomization) {
      dispatch({ type: ActionType.submitStarted });

      putEmailCustomization(currentProject?.id, state.emailCustomization)
        .then((res) => {
          showSuccessNotify();
          dispatch({ type: ActionType.submitSuccess, payload: res });
        })
        .catch((err) => {
          showErrorNotify(err);
          dispatch({ type: ActionType.submitFailed });
        });
    }
  };

  const renderHTMLPreview = () => {
    return (
      <StyledIframe title="email_frame">
        <div
          data-test-id="email-frame"
          style={{ display: 'grid', height: '100%' }}
          dangerouslySetInnerHTML={
            state.emailCustomization ? { __html: selectCurrentEmail(state) } : { __html: '' }
          }
        />
      </StyledIframe>
    );
  };

  if (state.fetching || fetchingAgreement) {
    return (
      <VerticalHorizontalCenterBlock>
        <Preloader />
      </VerticalHorizontalCenterBlock>
    );
  }

  return (
    <>
      {!isAgreementSigned && <AgreementNotification />}

      <HeaderTitle
        title={intl.formatMessage({ id: 'email-customization.title' })}
        subtitle={intl.formatMessage({ id: 'email-customization.subtitle' })}
      />

      <CustomizationFrameWrapper>
        <SettingsBar
          label={intl.formatMessage({ id: 'email-customization.settings.bar.label' })}
          popperLabel={intl.formatMessage({
            id: `email-customization.settings.bar.options.${state.emailType}`
          })}
          options={EmailTypeOptions}
          switchPreview={switchPreview}
          isPreview={state.isPreview}
          // ToDo: remove when email customization ready
          disableCustomization={!isAgreementSigned || true}
        />
        {state.isPreview ? (
          renderHTMLPreview()
        ) : (
          <CodeEditorFrame>
            <CodeEditor
              initialValue={selectCurrentEmail(state)}
              onChange={onCodeChange}
              showLineNumbers
            />
          </CodeEditorFrame>
        )}
      </CustomizationFrameWrapper>

      <FormFooter
        onSubmit={SubmitHandle}
        onCancel={backToNavigation}
        fetching={state.submitting}
        disabled={!isAgreementSigned || !!state.inputError}
        error={state.submitError}
      />
    </>
  );
};

export default withRouter(EmailPreviewFrame);
